import { Link } from "react-router-dom";
import { ShieldCheck, Factory, Globe2, ArrowRight, Building2 } from "lucide-react";
import { ROUTES, SITE } from "../site";
import { usePageMeta } from "../hooks/usePageMeta";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import WavyDivider from "../components/WavyDivider";
import GummyBurger from "../components/GummyBurger";

const meta = ROUTES[2];

const FACTS = [
  { label: "Perusahaan", value: "PT Yupi Indo Jelly Gum Tbk" },
  { label: "Berdiri", value: "1996" },
  { label: "Kantor Pusat", value: "Gunung Putri, Kabupaten Bogor, Jawa Barat" },
  { label: "Kategori Produk", value: "Kembang gula lunak jeli (gummy)" },
  { label: "Sertifikasi", value: "Halal MUI & BPJPH, terdaftar di BPOM" },
  { label: "Jangkauan", value: "Diekspor ke lebih dari 40 negara" },
];

export default function Tentang() {
  usePageMeta(meta.title, meta.description);

  return (
    <div data-testid="tentang-page">
      <Header />
      <main>
        <Breadcrumbs current="Tentang" />
        <section className="container-site grid grid-cols-1 items-center gap-10 pb-14 pt-8 lg:grid-cols-2">
          <div>
            <span className="eyebrow-chip">Tentang Kami</span>
            <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-tight tracking-tight text-plum sm:text-5xl">
              Gummy Burger Ikonik dari Dapur Gummy Indonesia
            </h1>
            <p className="mt-5 text-base leading-relaxed text-plum-soft sm:text-lg">
              Yupi Burger adalah salah satu bentuk gummy paling dikenal dari Yupi — permen kenyal
              berbentuk burger mini yang lengkap dengan lapisan roti, selada, keju, dan patty.
              Bentuknya yang unik menjadikannya camilan yang gampang diingat dan seru dimakan.
            </p>
          </div>
          <div className="flex justify-center">
            <GummyBurger
              variant="max"
              className="w-full max-w-sm animate-float"
              title="Ilustrasi Yupi Burger Max"
            />
          </div>
        </section>

        <section aria-labelledby="perusahaan-heading" className="bg-lettuce-soft">
          <WavyDivider fill="#FFFDF9" flip />
          <div className="container-site grid grid-cols-1 gap-10 py-14 sm:py-20 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="eyebrow-chip">Perusahaan</span>
              <h2
                id="perusahaan-heading"
                className="mt-4 font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl lg:text-4xl"
              >
                Diproduksi PT Yupi Indo Jelly Gum Tbk
              </h2>
              <p className="mt-4 text-base leading-relaxed text-plum-soft">
                Yupi adalah produsen kembang gula lunak jeli asal Indonesia yang berdiri sejak 1996.
                Fasilitas produksinya berada di Gunung Putri, Kabupaten Bogor, Jawa Barat, serta di
                Karanganyar, Jawa Tengah. Pada Maret 2025, Yupi resmi tercatat di Bursa Efek
                Indonesia dengan kode saham YUPI.
              </p>
              <p className="mt-4 text-base leading-relaxed text-plum-soft">
                Dikenal lewat bentuk-bentuk gummy yang unik — mulai dari burger, pizza, sampai cacing
                — produk Yupi kini diekspor ke lebih dari 40 negara.
              </p>
            </div>
            <div className="rounded-3xl border-2 border-plum/10 bg-white p-6 shadow-gummy sm:p-8">
              <h3 className="flex items-center gap-2 font-display text-lg font-bold text-plum">
                <Building2 className="h-5 w-5 text-bun-dark" aria-hidden="true" />
                Fakta Perusahaan
              </h3>
              <dl className="mt-5 space-y-3.5 text-sm" data-testid="tentang-company-facts">
                {FACTS.map((fact) => (
                  <div key={fact.label} className="flex flex-col gap-0.5 border-b border-dashed border-plum/10 pb-3 last:border-0 last:pb-0 sm:flex-row sm:gap-3">
                    <dt className="w-36 shrink-0 font-bold text-plum">{fact.label}</dt>
                    <dd className="text-plum-soft">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          <WavyDivider fill="#FFFDF9" />
        </section>

        <section aria-labelledby="jaminan-heading" className="container-site py-14 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <span className="eyebrow-chip">Jaminan Kualitas</span>
            <h2
              id="jaminan-heading"
              className="mt-4 font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl lg:text-4xl"
            >
              Kenyal yang Bisa Dipercaya
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div data-testid="tentang-quality-halal" className="card-gummy text-center">
              <span className="mx-auto inline-flex rounded-2xl bg-lettuce-soft p-4 text-lettuce-dark">
                <ShieldCheck className="h-8 w-8" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-display text-lg font-bold text-plum">Halal MUI &amp; BPJPH</h3>
              <p className="mt-2 text-sm leading-relaxed text-plum-soft">
                Produk Yupi tersertifikasi halal dari MUI dan BPJPH, dengan label halal yang tertera
                jelas pada kemasan.
              </p>
            </div>
            <div data-testid="tentang-quality-bpom" className="card-gummy text-center">
              <span className="mx-auto inline-flex rounded-2xl bg-bun-soft p-4 text-bun-dark">
                <Factory className="h-8 w-8" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-display text-lg font-bold text-plum">Terdaftar di BPOM</h3>
              <p className="mt-2 text-sm leading-relaxed text-plum-soft">
                Yupi Burger terdaftar di Badan Pengawas Obat dan Makanan (BPOM) sebagai kembang gula
                lunak jeli.
              </p>
            </div>
            <div data-testid="tentang-quality-global" className="card-gummy text-center">
              <span className="mx-auto inline-flex rounded-2xl bg-patty-soft p-4 text-patty-dark">
                <Globe2 className="h-8 w-8" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-display text-lg font-bold text-plum">Standar Ekspor</h3>
              <p className="mt-2 text-sm leading-relaxed text-plum-soft">
                Dengan jangkauan ekspor ke lebih dari 40 negara, produksi Yupi mengikuti standar
                mutu untuk pasar internasional.
              </p>
            </div>
          </div>
          <div className="mt-12 text-center">
            <Link to="/produk/" data-testid="tentang-cta-products" className="btn-primary">
              Lihat Varian Produk
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section aria-label="Informasi resmi" className="bg-plum">
          <WavyDivider fill="#FFFDF9" flip />
          <div className="container-site py-14 text-center sm:py-16">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Informasi Resmi Perusahaan
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
              Untuk informasi korporat, karier, dan berita resmi seputar {SITE.company}, kunjungi
              situs resmi Yupi.
            </p>
            <a
              href={SITE.official.corporate}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tentang-cta-official-site"
              className="btn-primary mt-7"
            >
              Kunjungi yupi.co.id
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
